import type { Epic } from '../../index.ts'
import type { Action } from '@reduxjs/toolkit'
import { cubeStateKey, visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.ts'
import { tableFromIPC } from 'apache-arrow'
import type { Observable } from 'rxjs'
import { EMPTY, catchError, concat, defer, from, groupBy, identity, map, mergeMap, of, switchMap } from 'rxjs'
import type { CubeFetchResult } from '../../../Repository/Visualization/Cubes.ts'
import { fetchCube } from '../../../Repository/Visualization/Cubes.ts'
import { retryCubeFetch } from '../../../Repository/Visualization/cubeRetry.ts'
import type { CachedCube } from '../../../Repository/Visualization/CubeCache.ts'
import { getCachedCube, putCachedCube } from '../../../Repository/Visualization/CubeCache.ts'
import { getCubeTable, registerCubeTable } from '../../../Repository/Visualization/CubeTableRegistry.ts'
import { recordCubeDiag } from '../../../Repository/Visualization/CubeDiagnostics.ts'
import type { BlastSource } from '../../../Model/Project/BlastSource.ts'
import type { SpinSelection } from '../../../Repository/Visualization/CubeKey.ts'

/**
 * Loads the visualization cube for `(scopeId, blastType)` whenever
 * `visualizationSlice.cubeRequested` is dispatched.
 *
 * Per cube key, requests are serialised with `switchMap` so a rapid blast-type flip
 * cancels the in-flight request rather than racing it. Across distinct keys, requests
 * proceed in parallel — different blast types for the same scope can prefetch alongside
 * each other.
 *
 * ## Stale-while-revalidate
 *
 * `cubeRequested` flips the slice entry to `'loading'` (the loading badge). To avoid that badge
 * flashing every time the workspace is re-entered — `VizLoaderPage`'s effect dispatches `reset()`,
 * wiping the `cubes` slice, so `cubeAutoRequestEpic` always re-issues `cubeRequested` — we render
 * any local copy of the cube *first* and only then revalidate in the background:
 *
 *   1. **Resident table** (the decoded `Table` in `CubeTableRegistry`, which survives `reset()`):
 *      emit `cubeReady` synchronously — the badge never paints on in-session re-entry.
 *   2. else **cached bytes** (IndexedDB, survives a hard reload): decode + register + `cubeReady`
 *      as soon as the (async) cache read returns — only a sub-frame gap, no network wait.
 *   3. **Revalidate** in the background via `fetchCube(scopeId, blastType, cachedEtag?)`:
 *      - `200`: newer data on the server → persist + register + `cubeReady` (swaps the stale table).
 *      - `304`: the local copy is current → nothing to do (already rendered above).
 *      - error: keep showing the local copy — a failed revalidation must not blow away a good cube.
 *   4. **Cold miss** (no resident table, no cached bytes): the badge is correct — fetch, then
 *      `cubeReady` / `cubeError` (a 304 with no local cache is a server/stale-etag race → refetch).
 *
 * Tables themselves never live in Redux; see `Repository/Visualization/CubeTableRegistry.ts`.
 *
 */
export const cubeLoaderEpic: Epic = (action$, _state$, { [visualizationSlice.name]: visualization }) =>
  action$.pipe(
    filterCubeRequested(visualization.cubeRequested.match),
    groupBy(({ scopeId, blastType, spin }) => cubeStateKey(scopeId, blastType, spin)),
    mergeMap((perKey$) =>
      perKey$.pipe(
        switchMap(({ scopeId, blastType, spin }) =>
          loadOne(scopeId, blastType, spin, visualization)
        )
      )
    )
  )

/**
 * `filter(...).map(...)` would be the obvious form, but we want strong typing of the payload
 * downstream — pulling out a tiny helper avoids ten lines of `as` casts at the call site.
 */
function filterCubeRequested(
  match: (action: unknown) => boolean
) {
  return (source$: Observable<unknown>) =>
    source$.pipe(
      mergeMap((action): Observable<{ scopeId: string; blastType: BlastSource; spin?: SpinSelection }> => {
        if (!match(action)) {
          return of()
        }
        const payload = (action as { payload: { scopeId: string; blastType: BlastSource; spin?: SpinSelection } }).payload
        return of(payload)
      })
    )
}

function loadOne(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinSelection | undefined,
  visualization: typeof visualizationSlice.actions
) {
  const key = cubeStateKey(scopeId, blastType, spin)

  // (1) Synchronous fast path: a decoded table is already resident (it survives `reset()`), so
  // mark the cube ready immediately — the loading badge never flashes on workspace re-entry. The
  // etag is unknown here (it lives in IndexedDB), but it's only metadata: revalidation reads the
  // authoritative etag from the cache below, and a 200 there will overwrite this with the real one.
  const resident = getCubeTable(scopeId, blastType, spin)
  const immediate$: Observable<Action> = resident
    ? of(visualization.cubeReady({ key, etag: null, loadedAt: Date.now() }))
    : EMPTY

  const work$ = defer(() => from(getCachedCube(scopeId, blastType, spin))).pipe(
    switchMap((cached) => {
      // (2) Cached bytes but no resident table (typical after a hard reload): decode + register +
      // render from cache before revalidating, so we still skip the network-bound spinner.
      const renderFromCache$: Observable<Action> =
        !resident && cached
          ? of(renderCachedTable(scopeId, blastType, spin, cached, visualization))
          : EMPTY

      const haveLocal = !!resident || !!cached
      return concat(
        renderFromCache$,
        revalidate(scopeId, blastType, spin, cached, haveLocal, visualization)
      )
    })
  )

  return concat(immediate$, work$)
}

/** Decode cached IPC bytes, register the table, and produce the `cubeReady` action. */
function renderCachedTable(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinSelection | undefined,
  cached: CachedCube,
  visualization: typeof visualizationSlice.actions
): Action {
  const table = tableFromIPC(cached.bytes)
  registerCubeTable(scopeId, blastType, table, cached.bytes.byteLength, spin)
  recordCubeDiag({
    kind: 'loaded',
    key: cubeStateKey(scopeId, blastType, spin),
    bytes: cached.bytes.byteLength,
    rows: table.numRows,
    detail: 'cache-render',
  })
  return visualization.cubeReady({ key: cubeStateKey(scopeId, blastType, spin), etag: cached.etag, loadedAt: Date.now() })
}

/**
 * Background revalidation. [haveLocal] is true when a resident table or cached bytes have already
 * been rendered, which changes the 304 / error handling to stale-while-revalidate semantics.
 */
function revalidate(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinSelection | undefined,
  cached: CachedCube | undefined,
  haveLocal: boolean,
  visualization: typeof visualizationSlice.actions
): Observable<Action> {
  const key = cubeStateKey(scopeId, blastType, spin)
  return defer(() => fetchCube(scopeId, blastType, cached?.etag ?? undefined, spin)).pipe(
    // A rendered cube makes this a background revalidation, not worth spending the rate limit on.
    haveLocal ? identity : retryCubeFetch(),
    mergeMap((res: CubeFetchResult) => {
      const now = Date.now()
      if (res.kind === 'fresh') {
        // Newer data on the server — swap in the fresh table + persist, even over a stale render.
        return from(persistFresh(scopeId, blastType, spin, res, now)).pipe(
          map(() => visualization.cubeReady({ key, etag: res.etag, loadedAt: now }))
        )
      }
      // 304 unchanged.
      if (haveLocal) {
        // Already rendered the resident table / cached bytes above — nothing left to do.
        return EMPTY
      }
      // 304 with no local cache is a server bug or a stale-etag race; refetch unconditionally.
      return defer(() => fetchCube(scopeId, blastType, undefined, spin)).pipe(
        retryCubeFetch(),
        map((freshRes) => ingestFresh(scopeId, blastType, spin, freshRes, visualization, now)),
        catchError((err) => of(visualization.cubeError({ key, error: String(err) })))
      )
    }),
    catchError((err) =>
      // Stale-while-revalidate: keep showing a cube we already have rather than flipping to error.
      haveLocal ? EMPTY : of(visualization.cubeError({ key, error: String(err) }))
    )
  )
}

async function persistFresh(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinSelection | undefined,
  res: Extract<CubeFetchResult, { kind: 'fresh' }>,
  now: number
): Promise<void> {
  // The Arrow Table's underlying buffers are accessible via .serialize() to round-trip into the cache;
  // we keep them as Uint8Array so the cache can store them and the next session can decode without
  // a network round-trip.
  const bytes = res.bytes
  registerCubeTable(scopeId, blastType, res.table, bytes.byteLength, spin)
  recordCubeDiag({
    kind: 'loaded',
    key: cubeStateKey(scopeId, blastType, spin),
    bytes: bytes.byteLength,
    rows: res.table.numRows,
    detail: 'fresh'
  })
  await putCachedCube(scopeId, blastType, {
    bytes,
    etag: res.etag,
    loadedAt: now
  }, spin)
}

function ingestFresh(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinSelection | undefined,
  res: CubeFetchResult,
  visualization: typeof visualizationSlice.actions,
  now: number
) {
  if (res.kind === 'unchanged') {
    // Shouldn't happen on the retry path, but treat defensively as an error
    return visualization.cubeError({
      key: cubeStateKey(scopeId, blastType, spin),
      error: 'received 304 on cache-bypass retry'
    })
  }
  registerCubeTable(scopeId, blastType, res.table, res.bytes.byteLength, spin)
  recordCubeDiag({
    kind: 'loaded',
    key: cubeStateKey(scopeId, blastType, spin),
    bytes: res.bytes.byteLength,
    rows: res.table.numRows,
    detail: 'fresh-refetch'
  })
  void putCachedCube(scopeId, blastType, {
    bytes: res.bytes,
    etag: res.etag,
    loadedAt: now
  }, spin)
  return visualization.cubeReady({
    key: cubeStateKey(scopeId, blastType, spin),
    etag: res.etag,
    loadedAt: now
  })
}
